/**
 * Word-level inline diff (Codex visual diff, ADR-006). Given two short strings —
 * a liturgy line, a block, a title — produce the run of "kept / added / removed"
 * pieces a reader sees as struck-through old words + highlighted new words.
 *
 * Hand-rolled (no diff dependency — `@cca/corpus-shared` carries zero runtime deps,
 * a deliberate "software-independent record" property). Inputs are tens of words,
 * so a textbook O(n·m) longest-common-subsequence is ample. The diff is over WORD
 * tokens (not characters), with whitespace kept as its own tokens so re-joining is
 * lossless and spacing renders correctly.
 */

export type WordOp = { type: 'same' | 'add' | 'del'; text: string };

/** Split into alternating word / whitespace tokens; `tokens.join('')` is lossless. */
function tokenize(s: string): string[] {
  return s.match(/\s+|\S+/g) ?? [];
}

/**
 * Word ops turning `before` into `after`. `same` runs are unchanged text, `del`
 * is in `before` only, `add` is in `after` only. Adjacent same-type ops are
 * coalesced so the UI renders runs, not a span per word.
 */
export function diffWords(before: string, after: string): WordOp[] {
  const a = tokenize(before);
  const b = tokenize(after);
  const m = a.length;
  const n = b.length;

  // dp[i][j] = LCS length of a[i..] and b[j..]; filled bottom-up. `?? 0` / `?? ''`
  // satisfy noUncheckedIndexedAccess without non-null assertions — every index here
  // is in bounds, so the fallbacks never fire (tokens are never the empty string).
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    const row = dp[i] ?? [];
    const next = dp[i + 1] ?? [];
    for (let j = n - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? (next[j + 1] ?? 0) + 1 : Math.max(next[j] ?? 0, row[j + 1] ?? 0);
    }
  }

  const raw: WordOp[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    const ai = a[i] ?? '';
    const bj = b[j] ?? '';
    if (ai === bj) {
      raw.push({ type: 'same', text: ai });
      i++;
      j++;
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      raw.push({ type: 'del', text: ai });
      i++;
    } else {
      raw.push({ type: 'add', text: bj });
      j++;
    }
  }
  while (i < m) raw.push({ type: 'del', text: a[i++] ?? '' });
  while (j < n) raw.push({ type: 'add', text: b[j++] ?? '' });

  // Coalesce adjacent same-type ops (incl. the whitespace tokens between words).
  const out: WordOp[] = [];
  for (const op of raw) {
    const last = out[out.length - 1];
    if (last && last.type === op.type) last.text += op.text;
    else out.push({ ...op });
  }
  return out;
}
